import React, { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as routes from 'constants/routes';
import { ButtonFilled } from 'components/ui/Button';
import Modal from 'components/layout/Modal';
import TermConsent from 'components/TermConsent';
import TournamentCountdown from 'components/game/TournamentCountdown';
import { joinTournament } from 'api/tournamentService';
import { useTournament } from 'context/TournamentContext';
import { useUser } from 'api/userService';
import Cookies from 'js-cookie';
import { getCookieDomainFromUrl } from 'utils';
import PopupImg from '../../../assets/Icon/gift-solid-full.svg';
import { registerAnonymousUser } from 'utils/auth/anonymousRegistration';
import { PaymentStatus } from 'types/payment';
import { KUCOIN_CONSENT_TEXT, isKucoinConsentTournament } from '../kucoinConsent';

import {
  Title,
  RowWrap,
  Row,
  Time,
  Text,
  Amount,
  StatePpl,
  Load,
  LoadWrap,
  TextSub,
  ContentCheckbox,
  RowCheckbox,
  CloseModal,
  TimeText,
  ModalSimple,
  ClosePopup,
  ContentModalSimple,
  ModalBackground,
  PaymentBlockMessage,
  ButtonLoading,
} from './styled';

type JoinPagePaymentContext = {
  pendingPaymentId?: string;
  currentPayment?: { id?: string } | null;
};

interface JoinPageProps {
  tournamentDetails: any;
  paymentContext?: JoinPagePaymentContext;
}

const JoinPage = ({ tournamentDetails, paymentContext }: JoinPageProps) => {
  const [isOpenedModal, setIsOpenedModal] = useState(false);
  const [isOpenedPopup, setIsOpenedPopup] = useState(false);
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const kucoinConsentRef = useRef<HTMLInputElement>(null);
  const [agree, setAgree] = useState(false);
  const [isload, setLoad] = useState(false);
  // Guards the whole entry operation (anonymous registration + join + redirect).
  // A ref, not `isload`, because setState is async: two clicks landing in the
  // same tick would both pass the state check and register two Anonymous Users,
  // orphaning the first one — the backend cannot dedupe them, they carry
  // different JWTs.
  const joinInFlightRef = useRef(false);
  const [isAnonymousUser, setAnonymousUser] = useState(false);
  const { isLoggedIn } = useUser();
  const { 
    tournamentStatus, 
    joinedStatus,
    isPaidTournament,
    isPaymentRequired,
    userPaymentStatus,
  } = useTournament();

  // Payment blocking logic
  const isPaymentBlocked = isPaidTournament && isPaymentRequired && 
    userPaymentStatus !== 'CONFIRMED' && userPaymentStatus !== 'USED';

  const getPaymentBlockMessage = (): string | null => {
    if (!isPaymentBlocked) return null;
    switch (userPaymentStatus) {
      case 'PENDING':
        return 'Waiting for payment confirmation...';
      case 'FAILED':
        return 'Payment failed - please retry';
      case 'EXPIRED':
        return 'Payment expired - please create new payment';
      default:
        return 'Payment required to join';
    }
  };
  const navigate = useNavigate();
  const currentDate = new Date(); // get the current date
  // TB-RULE-DATA-NORMALIZE: backend returns flat Strapi entity; legacy fixtures
  // still use envelope shape — 3-step fallback handles both.
  const rawData: any = tournamentDetails?.data;
  const attrs: any = rawData?.attributes ?? rawData?.data ?? rawData ?? {};
  const participantsRaw: any = attrs?.participants;
  const participantsArray: any[] = Array.isArray(participantsRaw?.data)
    ? participantsRaw.data
    : Array.isArray(participantsRaw)
      ? participantsRaw
      : [];
  const startDate = new Date(attrs?.start_at);
  const endDate = new Date(attrs?.end_at);
  const domain = getCookieDomainFromUrl(process.env.REACT_APP_LANDING_PAGE);
  let timeLabel = "starts in";
  let countdownDate = startDate;
  const rewardPool = attrs?.reward_pool;
  const rewardPoolCurrency = attrs?.reward_pool_currency ?? 'USDT';
  const displayRewardPool = rewardPool > 0 ? rewardPool + ' ' + rewardPoolCurrency : '---';
  const numberOfParticipants = participantsArray.length;
  const tournamentId = rawData?.id;
  const tournamentAnonymousUsers = attrs?.allow_anonymous_users;

  const checkboxHandler = () => {
    if (ref1.current.checked && ref2.current.checked && ref3.current.checked) {
      setAgree(!agree);
    } else {
      setAgree(false);
    }
  }

  if (startDate < currentDate) {
    timeLabel = "ends in";
    countdownDate = endDate;
  }

  let loadTime;
  if (!tournamentDetails || isNaN(countdownDate.getTime())) {
    loadTime = <Load><LoadWrap></LoadWrap></Load>;
  } else {
    loadTime = <TournamentCountdown date={countdownDate.toISOString()} />;
  }

  const resolvePendingPaymentId = () => {
    return paymentContext?.pendingPaymentId ?? paymentContext?.currentPayment?.id;
  };

  const handleLogInClick = async () => {
    try {
      setLoad(!isload);
      if (!isLoggedIn) {
        localStorage.setItem('intendToJoinTournament', tournamentId);
        Cookies.set('intendToJoinTournament', tournamentId, { expires: 7, secure: true, sameSite: 'none', domain });
        setAgree(false);
        navigate(routes.HOME);
        return;
      }

      const newPath = `${routes.TOURNAMENT.replace(':id', tournamentId)}`;
      window.location.href = newPath;

    } catch (error) {
      if (error.error.status === 409 && error.error.name === 'ConflictError') {
        window.location.href = `${routes.GAME.replace(':id', tournamentId)}`;
      } else {
        console.error('Error joining tournament:', error);
      }
    }
  };

  const handleJoinClick = async () => {
    if (joinInFlightRef.current) return;
    joinInFlightRef.current = true;
    try {
      setLoad(true);
      const hash = new URLSearchParams(window.location.search).get('hash');
      const kucoinConsent = Boolean(kucoinConsentRef.current?.checked);

      if (!isLoggedIn) {
        localStorage.setItem('intendToJoinTournament', tournamentId);
        Cookies.set('intendToJoinTournament', tournamentId, { expires: 7, secure: true, sameSite: 'none', domain });
        setAgree(false);
        joinInFlightRef.current = false;
        setLoad(false);
        navigate(routes.HOME);
        return;
      }

      if (!joinedStatus) {
        const paymentId = resolvePendingPaymentId();
        if (paymentId) {
          await joinTournament(tournamentId, hash, paymentId);
        } else {
          await joinTournament(tournamentId, hash, undefined, kucoinConsent);
        }
      }

      const newPath = `${routes.TOURNAMENT.replace(':id', tournamentId)}`;
      window.location.href = newPath;

    } catch (error: any) {
      joinInFlightRef.current = false;
      setLoad(false);

      // Payment-specific errors are pre-mapped to a UI state by tournamentService
      if (error?.uiState) {
        console.error('Payment error:', error);
        alert(`Payment error: ${error.uiState}. Please try again or contact support.`);
        return;
      }

      // Standard Strapi error envelope: { error: { status, name, message, details: { errorCode } } }
      const apiError = error?.error;
      const errorCode = apiError?.details?.errorCode;

      if (errorCode === 'USER_ALREADY_PARTICIPANT') {
        window.location.href = `${routes.GAME.replace(':id', tournamentId)}`;
        return;
      }

      // Preserve specific UX hint for the only error a normal user can self-correct
      if (errorCode === 'ANONYMOUS_NOT_ALLOWED') {
        alert('Auto-generated users are not allowed in this tournament. Logout and try again.');
        return;
      }

      console.error('Error joining tournament:', error);
      alert(apiError?.message || 'An error occurred while joining the tournament. Please try again.');
    }
  };

  const handleJoinClickRandomUser = async (event?: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    if (joinInFlightRef.current) return;
    joinInFlightRef.current = true;
    try {
      setLoad(true);
      const hash = new URLSearchParams(window.location.search).get('hash');

      // Register anonymous user and store credentials
      const credentials = await registerAnonymousUser();

      // Store user information
      localStorage.setItem('username', credentials.username);
      localStorage.setItem('intendToJoinTournament', tournamentId);
      localStorage.setItem('email', credentials.email);

      // Join tournament if not already joined
      if (!joinedStatus) {
        const paymentId = resolvePendingPaymentId();
        if (paymentId) {
          await joinTournament(tournamentId, hash, paymentId);
        } else {
          await joinTournament(tournamentId, hash);
        }
      }

      // Navigate to tournament lobby
      const lobbyPath = routes.TOURNAMENT.replace(':id', tournamentId);
      window.location.href = lobbyPath;

    } catch (error: any) {
      console.error('Error joining tournament:', error);
      joinInFlightRef.current = false;
      setLoad(false);

      // Handle payment-specific errors (format from 14.3)
      if (error?.uiState) {
        alert(`Payment error: ${error.uiState}. Please try again or contact support.`);
        return;
      }
      
      // Handle standard API errors
      if (error?.error?.status === 409 && error?.error?.name === 'ConflictError') {
        // User already in tournament, redirect to tournament lobby
        const lobbyPath = routes.TOURNAMENT.replace(':id', tournamentId);
        window.location.href = lobbyPath;
      } else {
        alert('An error occurred while joining the tournament. Please try again.');
      }
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const autoJoin = searchParams.get('autojoin');

    if (tournamentId === 235 || tournamentId === 237 ) {
      setIsOpenedPopup(true);
    }
    if (tournamentAnonymousUsers === false) {
      setAnonymousUser(false);
    }
    if (tournamentAnonymousUsers === true) {
      setAnonymousUser(true);
      // Automatically join if autojoin=true and it's a random user tournament
      if (autoJoin === 'true' && !joinedStatus && (tournamentStatus === 'IN_PROGRESS' || tournamentStatus === 'BEFORE_START')) {
        handleJoinClickRandomUser();
      }
    }
  }, [tournamentId, tournamentAnonymousUsers, joinedStatus, tournamentStatus]);

  return (
    <div>
      <Title>
        {attrs?.name}
        {(tournamentStatus === 'IN_PROGRESS' || tournamentStatus === 'FINISHED') ? (
          <span></span>
        ) : <span>{timeLabel}</span>}
      </Title>
      <RowWrap>
        {joinedStatus && (tournamentStatus === 'FINISHED') ? (
          <Time><TimeText>Finished</TimeText></Time>
        ) : <Time>{loadTime}</Time>}
        <Row>
          <div>
            <Text>Reward pool</Text>
            <Amount>{displayRewardPool}</Amount>
          </div>
          <div>
            <Text>Participants</Text>
            <StatePpl><b>{numberOfParticipants}</b>/No limits</StatePpl>
          </div>
        </Row>

        {!joinedStatus && (tournamentStatus === 'IN_PROGRESS' || tournamentStatus === 'BEFORE_START') ? (
          <>
            {/* Payment blocking message */}
            {isPaymentBlocked && (
              <PaymentBlockMessage>
                {getPaymentBlockMessage()}
              </PaymentBlockMessage>
            )}
            
            {isAnonymousUser ? (
              <>
                <ButtonFilled
                  type="submit"
                  onClick={handleJoinClickRandomUser}
                  disabled={isPaymentBlocked || isload}
                >
                  {isload ? (
                    <ButtonLoading>
                      <LoadWrap className="button_load" />
                      Joining…
                    </ButtonLoading>
                  ) : 'Join'}
                </ButtonFilled>
                <TextSub>
                  Or choose other tournaments <Link to={'https://tradingbattles.com/#upcoming-tournaments'}>here</Link>
                </TextSub>
              </>
            ) :

              <div>
                {!isLoggedIn && (tournamentStatus === 'IN_PROGRESS' || tournamentStatus === 'BEFORE_START') ? (
                  <>
                    <ButtonFilled type="submit" onClick={handleLogInClick}>
                      Log in
                    </ButtonFilled>
                    <TextSub>
                      Or choose other tournaments <Link to={'https://tradingbattles.com/#upcoming-tournaments'}>here</Link>
                    </TextSub>
                  </>
                ) :
                  <>
                    <ButtonFilled 
                      type="submit" 
                      onClick={() => setIsOpenedModal(!isOpenedModal)}
                      disabled={isPaymentBlocked}
                    >
                      Join
                    </ButtonFilled>
                    <TextSub>
                      Or choose other tournaments <Link to={'https://tradingbattles.com/#upcoming-tournaments'}>here</Link>
                    </TextSub>
                  </>
                }
              </div>

            }

          </>
        ) : null}
      </RowWrap>

      {!joinedStatus && (tournamentStatus === 'IN_PROGRESS' || tournamentStatus === 'BEFORE_START') ? (
        <>
          {isOpenedPopup && (
            <ModalSimple className="simpleTextModalWrap">
              <ModalBackground onClick={() => setIsOpenedPopup(!isOpenedPopup)} />
              <ContentModalSimple className="simpleTextModal">
                <ClosePopup onClick={() => setIsOpenedPopup(!isOpenedPopup)} />
                <Title>INFORMACJA</Title>
                <img src={PopupImg} alt="Img Popup" />
                <TextSub>
                  Nagrody przewidziane są dla <strong>top 20</strong> graczy - tylko dla uczestników Cryptosphere, warunkiem otrzymania (odebrania) nagrody jest osobista obecność na terenie wydarzenia w dniu <strong>11.10.2025</strong> o godz. <strong>17:00 - 18:00</strong> pod sceną główną.
                </TextSub>
              </ContentModalSimple>
            </ModalSimple>
          )}
        </>
      ) : null}

      {isOpenedModal && (
        <Modal title="Join tournament">
          <CloseModal onClick={() => setIsOpenedModal(!isOpenedModal)} />
          <TermConsent />
          <ContentCheckbox>
            <div>
              <RowCheckbox>
                <label htmlFor="checkbox1" className="option">
                  <p>I accept <a target="_blank" rel="noopener noreferrer" href="https://tradingbattles.com/terms-of-use/">Terms of Use</a> of Trading Battles</p>
                  <input ref={ref1} type="checkbox" id="checkbox1" onChange={checkboxHandler} />
                  <span className="checkbox checkboxStyle"></span>
                </label>
                <label htmlFor="checkbox2" className="option">
                  <p>I accept <a target="_blank" rel="noopener noreferrer" href="https://tradingbattles.com/terms-and-conditions/">Terms & Conditions</a> of Trading Battles</p>
                  <input ref={ref2} type="checkbox" id="checkbox2" onChange={checkboxHandler} />
                  <span className="checkbox checkboxStyle"></span>
                </label>
                <label htmlFor="checkbox3" className="option">
                  <p>I accept <a target="_blank" rel="noopener noreferrer" href="https://tradingbattles.com/privacy-policy/">Privacy Policy</a> of Trading Battles</p>
                  <input ref={ref3} type="checkbox" id="checkbox3" onChange={checkboxHandler} />
                  <span className="checkbox checkboxStyle"></span>
                </label>
                {isKucoinConsentTournament(tournamentId) && (
                  <label htmlFor="checkboxKucoinConsent" className="option">
                    <p>{KUCOIN_CONSENT_TEXT}</p>
                    <input ref={kucoinConsentRef} type="checkbox" id="checkboxKucoinConsent" />
                    <span className="checkbox checkboxStyle"></span>
                  </label>
                )}
              </RowCheckbox>
            </div>
            <ButtonFilled disabled={!agree || isload} type="submit" onClick={handleJoinClick}>
              {isload ? (
                <ButtonLoading>
                  <LoadWrap className="button_load" />
                  Joining…
                </ButtonLoading>
              ) : "Ok"}
            </ButtonFilled>
          </ContentCheckbox>
        </Modal>
      )}
    </div>
  );
};
export default JoinPage;
